import { useEffect, useState, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { ErrorBoundary } from 'react-error-boundary';
import { LuTriangleAlert } from 'react-icons/lu';

import { useVideoProjectCreation } from '@contexts/VideoProjectCreation.context';
import { useToast } from '@contexts/Toast.context';
import { DEFAULT_MODEL_OPTION } from '@config/videoDetectionModelConfig';
import VideoPlayerSection from '../components/VideoPlayerSection';
import MetadataPanel from '../components/MetadataPanel';
import ConfigurationOptions from '../components/ConfigurationOptions';
import VirtualLineControls from '../components/VirtualLineControls';
import Button from 'react-bootstrap/Button';

const ConfigurationContainer = styled.div`
	display: grid;
	gap: 1.5rem;
	padding: 1rem 0;

	/* Desktop: Side by side */
	@media (min-width: 992px) {
		grid-template-columns: 1fr 400px;
	}

	/* Tablet/Mobile: Stacked */
	@media (max-width: 991px) {
		grid-template-columns: 1fr;
	}
`;

const VideoPanel = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	min-width: 0; /* Prevent overflow */
`;

const ConfigPanel = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;

	@media (min-width: 992px) {
		align-self: ${({ $stretch }) => ($stretch ? 'stretch' : 'start')};
	}

	/* Push last section to bottom-align with video panel when stretched */
	${({ $stretch }) => $stretch && `& > :last-child { margin-top: auto; }`}
`;

const ErrorContainer = styled.div`
	padding: 2rem;
	text-align: center;
	background: rgba(220, 53, 69, 0.1);
	border: 1px solid rgba(220, 53, 69, 0.3);
	border-radius: 0.375rem;
`;

const ErrorIcon = styled.div`
	margin-bottom: 1rem;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #dc3545;
`;

const ErrorTitle = styled.h3`
	color: #dc3545;
	margin-bottom: 0.5rem;
`;

const ErrorMessage = styled.p`
	color: rgba(255, 255, 255, 0.7);
	margin-bottom: 1rem;
`;

const ErrorFallback = ({ error, resetErrorBoundary }) => (
	<ErrorContainer>
		<ErrorIcon>
			<LuTriangleAlert size={48} />
		</ErrorIcon>
		<ErrorTitle>Something went wrong</ErrorTitle>
		<ErrorMessage>
			We hit a snag while loading this step. Please try again.
		</ErrorMessage>
		<Button onClick={resetErrorBoundary} variant="primary">
			Go Back
		</Button>
	</ErrorContainer>
);

const VideoPreviewAndConfiguration = () => {
	const { videoMetadata, setVideoMetadata, setConfiguration, previousStage } =
		useVideoProjectCreation();
	const { watch, setValue } = useFormContext();
	const { addToast } = useToast();

	const countingScenario = watch('countingScenario');
	const countingMethod = watch('countingMethod');
	const modelOption = watch('modelOption');
	const virtualLineDefinition = watch('virtualLineDefinition');
	const timeRange = watch('timeRange');

	// Canvas drawing state (lifted here so VirtualLineControls can live in ConfigPanel)
	const [drawMode, setDrawMode] = useState(false);
	const [startPoint, setStartPoint] = useState(null);
	const [endPoint, setEndPoint] = useState(null);

	const lineColor = watch('lineColor') || '#198754';
	const setLineColor = useCallback((color) => setValue('lineColor', color), [setValue]);

	const handleStartDrawing = () => {
		setDrawMode(true);
		setStartPoint(null);
		setEndPoint(null);
		setValue('virtualLineDefinition', null);
	};

	const handleClearLine = () => {
		setStartPoint(null);
		setEndPoint(null);
		setDrawMode(false);
		setValue('virtualLineDefinition', null);
	};

	// Reset form fields when video file changes (configuration is reset in context)
	useEffect(() => {
		if (!videoMetadata) {
			setValue('countingScenario', null);
			setValue('countingMethod', null);
			setValue('modelOption', DEFAULT_MODEL_OPTION);
			setValue('virtualLineDefinition', null);
			setValue('timeRange', null);
		}
	}, [videoMetadata, setValue]);

	// Update configuration in context when form values change
	useEffect(() => {
		setConfiguration({
			countingScenario: countingScenario?.value || null,
			countingMethod: countingMethod?.value || null,
			modelOption: modelOption || DEFAULT_MODEL_OPTION,
			virtualLineDefinition,
			timeRange,
		});
	}, [
		countingScenario,
		countingMethod,
		virtualLineDefinition,
		timeRange,
		setConfiguration,
		modelOption,
	]);

	// Auto-adjust counting method when scenario changes
	useEffect(() => {
		if (countingScenario?.value === 'moving_drone') {
			setValue('countingMethod', { value: 'unique_id', label: 'Unique Object' });
		}
	}, [countingScenario, setValue]);

	// Clear virtual line when switching away from line_based method
	useEffect(() => {
		if (countingMethod?.value !== 'line_based') {
			setValue('virtualLineDefinition', null);
			setDrawMode(false);
			setStartPoint(null);
			setEndPoint(null);
		}
	}, [countingMethod, setValue]);

	// Disable line_based method if video metadata is missing (video can't play)
	useEffect(() => {
		if (!videoMetadata?.duration && countingMethod?.value === 'line_based') {
			// Auto-switch to unique_id if video can't be previewed
			setValue('countingMethod', { value: 'unique_id', label: 'Unique Object' });

			addToast({
				id: 'virtual-line-disabled',
				title: 'Virtual Line Unavailable',
				message: 'Virtual line requires video preview. Switched to Unique Object method.',
				bg: 'warning',
			});
		}
	}, [videoMetadata, countingMethod, setValue, addToast]);

	return (
		<ErrorBoundary
			FallbackComponent={ErrorFallback}
			onReset={() => {
				previousStage();
			}}
			onError={error => {
				console.error('Video configuration error:', error);
			}}>
			<ConfigurationContainer>
				<VideoPanel>
					<VideoPlayerSection
						drawMode={drawMode}
						onDrawModeChange={setDrawMode}
						startPoint={startPoint}
						onStartPointChange={setStartPoint}
						endPoint={endPoint}
						onEndPointChange={setEndPoint}
						lineColor={lineColor}
					/>
				</VideoPanel>

				<ConfigPanel $stretch={countingMethod?.value === 'line_based'}>
					<MetadataPanel metadata={videoMetadata} />
					<ConfigurationOptions />
					{countingMethod?.value === 'line_based' && (
						<VirtualLineControls
							drawMode={drawMode}
							onStartDrawing={handleStartDrawing}
							onClearLine={handleClearLine}
							startPoint={startPoint}
							lineColor={lineColor}
							onLineColorChange={setLineColor}
						/>
					)}
				</ConfigPanel>
			</ConfigurationContainer>
		</ErrorBoundary>
	);
};

export default VideoPreviewAndConfiguration;
